import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, Drawer, Descriptions, Tag, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import {
  GET_ADDRESS_ERROR_RECORD_LIST,
  DELETE_ADDRESS_ERROR_RECORD,
  BATCH_DELETE_ADDRESS_ERROR_RECORD,
  GetAddressErrorRecordListResponse,
  AddressErrorRecordType,
  ADDRESS_ERROR_HANDLE_FLAG,
} from './graphql/addressErrorRecordGraphql';

const HANDLE_FLAG_COLOR_MAP: Record<number, string> = {
  [ADDRESS_ERROR_HANDLE_FLAG.UNHANDLED]: 'default',
  [ADDRESS_ERROR_HANDLE_FLAG.HANDLED_SUCCESS]: 'green',
  [ADDRESS_ERROR_HANDLE_FLAG.HANDLED_FAILED]: 'red',
};

const formatDate = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—');

const AddressErrorRecordPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [detailRecord, setDetailRecord] = useState<AddressErrorRecordType | null>(null);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressErrorRecordListResponse>(
    GET_ADDRESS_ERROR_RECORD_LIST,
    {
      variables: {
        originalZipcode: appliedFilters.originalZipcode || undefined,
        originalStreetAddress: appliedFilters.originalStreetAddress || undefined,
        errorType: appliedFilters.errorType,
        handleFlag: appliedFilters.handleFlag,
        createdAtFrom: appliedFilters.createdRange?.[0]?.toISOString(),
        createdAtTo: appliedFilters.createdRange?.[1]?.toISOString(),
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteRecord] = useMutation(DELETE_ADDRESS_ERROR_RECORD, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const [batchDeleteRecords, { loading: batchDeleting }] = useMutation(BATCH_DELETE_ADDRESS_ERROR_RECORD, {
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const showDeleteConfirm = (record: AddressErrorRecordType) => {
    Modal.confirm({
      title: t('mapTool.addressErrorRecord.deleteTitle') as string,
      content: t('mapTool.addressErrorRecord.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteRecord({ variables: { id: Number(record.id) } }),
    });
  };

  const handleBatchDelete = () => {
    if (selectedIds.length === 0) {
      message.warning(t('mapTool.addressErrorRecord.batchDelete.emptySelection') as string);
      return;
    }
    const ids = selectedIds.map((k) => Number(k));
    Modal.confirm({
      title: t('mapTool.addressErrorRecord.batchDelete.title') as string,
      content: t('mapTool.addressErrorRecord.batchDelete.confirmContent', { count: ids.length }) as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: async () => {
        try {
          const { data: resp } = await batchDeleteRecords({ variables: { ids } });
          const deleted = resp?.batchDeleteAddressErrorRecord ?? 0;
          message.success(
            t('mapTool.addressErrorRecord.batchDelete.successMsg', {
              requested: ids.length,
              deleted,
            }) as string
          );
          setSelectedIds([]);
          refetch();
        } catch (err) {
          message.error((err as Error).message);
        }
      },
    });
  };

  const columns: TableColumnsType<AddressErrorRecordType> = [
    {
      title: t('mapTool.addressErrorRecord.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressErrorRecord.table.originalFullAddress'),
      dataIndex: 'originalFullAddress',
      key: 'originalFullAddress',
      width: 320,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressErrorRecord.table.standardizedFullAddress'),
      dataIndex: 'standardizedFullAddress',
      key: 'standardizedFullAddress',
      width: 320,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressErrorRecord.table.errorType'),
      dataIndex: 'errorType',
      key: 'errorType',
      width: 140,
      align: 'center',
      render: (v: string | null | undefined) => (v ? t(`enum.addressErrorType.${v}`, v) : '—'),
    },
    {
      title: t('mapTool.addressErrorRecord.table.handleFlag'),
      dataIndex: 'handleFlag',
      key: 'handleFlag',
      width: 110,
      align: 'center',
      render: (v: number) => (
        <Tag color={HANDLE_FLAG_COLOR_MAP[v] ?? 'default'}>{t(`enum.addressErrorHandleFlag.${v}`, String(v))}</Tag>
      ),
    },
    {
      title: t('mapTool.addressErrorRecord.table.handleResult'),
      dataIndex: 'handleResult',
      key: 'handleResult',
      width: 200,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressErrorRecord.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) => formatDate(v),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" size="small" onClick={() => setDetailRecord(record)}>
            {t('components.button.view')}
          </Button>
          {record.handleFlag === ADDRESS_ERROR_HANDLE_FLAG.UNHANDLED ? (
            <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
              {t('components.button.delete')}
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const pageData = data?.addressErrorRecordList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressErrorRecord.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <Button
            danger
            loading={batchDeleting}
            onClick={handleBatchDelete}
            disabled={selectedIds.length === 0}
            className="w-auto min-w-24"
          >
            <span className="font-bold">
              {t('mapTool.addressErrorRecord.batchDelete.button')}
              {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
            </span>
          </Button>
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: setSelectedIds,
            getCheckboxProps: (record) => ({
              disabled: record.handleFlag !== ADDRESS_ERROR_HANDLE_FLAG.UNHANDLED,
            }),
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) =>
              t('mapTool.addressErrorRecord.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <Drawer
        title={t('mapTool.addressErrorRecord.detailTitle')}
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        width={800}
        placement="right"
      >
        {detailRecord && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.originalFullAddress')}>
              <span style={{ wordBreak: 'break-all' }}>{detailRecord.originalFullAddress}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.originalStreetAddress')}>
              {detailRecord.originalStreetAddress}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.originalBuilding')}>
              {detailRecord.originalBuilding || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.originalCity')}>
              {detailRecord.originalCity}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.originalState')}>
              {detailRecord.originalState}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.originalZipcode')}>
              {detailRecord.originalZipcode}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.addressId')}>
              {detailRecord.addressId ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.standardizedFullAddress')}>
              {detailRecord.standardizedFullAddress ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.standardizedStreetAddress')}>
              {detailRecord.standardizedStreetAddress ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.standardizedCity')}>
              {detailRecord.standardizedCity ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.standardizedState')}>
              {detailRecord.standardizedState ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.standardizedZipcode')}>
              {detailRecord.standardizedZipcode ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.errorType')}>
              {detailRecord.errorType ? t(`enum.addressErrorType.${detailRecord.errorType}`, detailRecord.errorType) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.handleFlag')}>
              {t(`enum.addressErrorHandleFlag.${detailRecord.handleFlag}`, String(detailRecord.handleFlag))}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.handleResult')}>
              {detailRecord.handleResult ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.remark')}>
              {detailRecord.remark ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.createdAt')}>
              {formatDate(detailRecord.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('mapTool.addressErrorRecord.detail.updatedAt')}>
              {formatDate(detailRecord.updatedAt)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AddressErrorRecordPage;
